import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Card,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";

import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import usePageTitle from "app/pageTitle";
import DataTable from "theme/Tables/DataTable";
import { useAwaitDialog } from "app/components/status_dialog";

const formatNumber = (value) => Number(value || 0).toLocaleString();

export default function InventoryTrackerSupervisorPage() {
  const { ref } = useParams();
  const navigate = useNavigate();
  usePageTitle("Supervisor Inventory");

  const statusDialog = useAwaitDialog();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");

  const [pageLoaded, setPageLoaded] = useState(false);
  const [refreshPage, setRefreshPage] = useState(false);

  const [tracker, setTracker] = useState(null);
  const [supervisor, setSupervisor] = useState(null);
  const [summary, setSummary] = useState({});
  const [products, setProducts] = useState([]);
  const [recent, setRecent] = useState([]);

  const [productData, setProductData] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);

  const [openIssueDialog, setOpenIssueDialog] = useState(false);
  const [openRecallDialog, setOpenRecallDialog] = useState(false);

  const [issueProduct, setIssueProduct] = useState("");
  const [issueQuantity, setIssueQuantity] = useState("");

  const [recallProduct, setRecallProduct] = useState("");
  const [recallQuantity, setRecallQuantity] = useState("");

  const handleClose = () => setOpen(false);

  const productColumns = [
    { Header: "Product", accessor: "name", align: "left" },
    { Header: "Available", accessor: "available", align: "center" },
    { Header: "Transfered", accessor: "transfered", align: "center" },
    { Header: "Locked", accessor: "locked", align: "center" },
    { Header: "Consumed", accessor: "consumed", align: "center" },
  ];

  const recentColumns = [
    { Header: "Product", accessor: "product", align: "left" },
    { Header: "Type", accessor: "entry_type", align: "center" },
    { Header: "Quantity", accessor: "quantity", align: "center" },
    { Header: "Date", accessor: "created", align: "right" },
  ];

  const buildProductRows = (data) => {
    const rows = [];

    data.map((row) => {
      rows.push({
        name: row.name,
        available: (
          <MDTypography variant="caption" color="dark" fontWeight="bold">
            {formatNumber(row.available)}
          </MDTypography>
        ),
        transfered: (
          <MDTypography variant="caption" color="info" fontWeight="medium">
            {formatNumber(row.transfered)}
          </MDTypography>
        ),
        locked: (
          <MDTypography variant="caption" color="warning" fontWeight="medium">
            {formatNumber(row.locked)}
          </MDTypography>
        ),
        consumed: (
          <MDTypography variant="caption" color="success" fontWeight="medium">
            {formatNumber(row.consumed)}
          </MDTypography>
        ),
      });

      return null;
    });

    return rows;
  };

  const buildRecentRows = (data) => {
    const rows = [];

    data.map((row) => {
      rows.push({
        product: row.product,
        entry_type: (
          <MDTypography
            variant="caption"
            color={
              row.entry_type === "LOCKED"
                ? "warning"
                : row.entry_type === "CONSUMED"
                ? "success"
                : row.entry_type === "RETURNED"
                ? "info"
                : row.entry_type === "YTANSFERED"
                ? "dark"
                : "text"
            }
            fontWeight="medium"
          >
            {row.entry_type}
          </MDTypography>
        ),
        quantity: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {formatNumber(row.quantity)}
          </MDTypography>
        ),
        created: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {row.created}
          </MDTypography>
        ),
      });

      return null;
    });

    return rows;
  };

  async function saveIssueStock() {
    if (!issueProduct || !issueQuantity) {
      setSeverity("error");
      setErrMsg("Please select a product and quantity.");
      setOpen(true);
      return;
    }

    setOpenIssueDialog(false);
    setShowLoading(true);

    await axios
      .post("/portal/products/inventory/trackers/supervisors/stocks/issue.json", {
        reference: ref,
        product: issueProduct,
        quantity: parseInt(issueQuantity, 10),
      })
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success", "Success!");

          setIssueProduct("");
          setIssueQuantity("");
          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("Unable to complete request.");
        setOpen(true);
      });
  }

  async function saveRecallStock() {
    if (!recallProduct || !recallQuantity) {
      setSeverity("error");
      setErrMsg("Please select a product and quantity.");
      setOpen(true);
      return;
    }

    setOpenRecallDialog(false);
    setShowLoading(true);

    await axios
      .post("/portal/products/inventory/trackers/supervisors/stocks/recall.json", {
        reference: ref,
        product: recallProduct,
        quantity: parseInt(recallQuantity, 10),
      })
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success", "Success!");

          setRecallProduct("");
          setRecallQuantity("");
          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("Unable to complete request.");
        setOpen(true);
      });
  }

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      await axios
        .get(`/portal/products/inventory/trackers/supervisors/${ref}/fetch.json`)
        .then((result) => {
          setShowLoading(false);

          if (result.data.status === "success") {
            setTracker(result.data.data.tracker);
            setSupervisor(result.data.data.supervisor);
            setSummary(result.data.data.summary || {});
            setProductData(result.data.data.products || []);
            setProducts(buildProductRows(result.data.data.products || []));
            setRecent(buildRecentRows(result.data.data.recent || []));
            setAvailableProducts(result.data.data.available_products || []);
            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [ref, refreshPage]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog open={openIssueDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Issue Stock</MDBox>
        </DialogTitle>

        <DialogContent>
          <MDBox mt={1}>
            <MDTypography variant="button" fontWeight="medium">
              {supervisor?.name}
            </MDTypography>
          </MDBox>

          <MDBox mt={3}>
            <MDInput
              select
              label="Product"
              fullWidth
              value={issueProduct}
              onChange={(e) => setIssueProduct(e.target.value)}
              SelectProps={{ style: { padding: "12px 0px" } }}
            >
              {availableProducts.map((row) => (
                <MenuItem key={row.reference} value={row.reference}>
                  {row.title}
                </MenuItem>
              ))}
            </MDInput>
          </MDBox>

          <MDBox mt={3}>
            <MDInput
              label="Quantity"
              type="number"
              fullWidth
              value={issueQuantity}
              onChange={(e) => setIssueQuantity(e.target.value)}
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenIssueDialog(false);
              setIssueProduct("");
              setIssueQuantity("");
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>

          <MDButton color="dark" variant="contained" onClick={saveIssueStock}>
            Issue
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openRecallDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Recall Stock</MDBox>
        </DialogTitle>

        <DialogContent>
          <MDBox mt={1}>
            <MDTypography variant="button" fontWeight="medium">
              {supervisor?.name}
            </MDTypography>
          </MDBox>

          <MDBox mt={3}>
            <MDInput
              select
              label="Product"
              fullWidth
              value={recallProduct}
              onChange={(e) => setRecallProduct(e.target.value)}
              SelectProps={{ style: { padding: "12px 0px" } }}
            >
              {productData.map((row) => (
                <MenuItem key={row.reference} value={row.reference}>
                  {row.name} ({formatNumber(row.available)})
                </MenuItem>
              ))}
            </MDInput>
          </MDBox>

          <MDBox mt={3}>
            <MDInput
              label="Quantity"
              type="number"
              fullWidth
              value={recallQuantity}
              onChange={(e) => setRecallQuantity(e.target.value)}
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenRecallDialog(false);
              setRecallProduct("");
              setRecallQuantity("");
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>

          <MDButton color="dark" variant="contained" onClick={saveRecallStock}>
            Recall
          </MDButton>
        </DialogActions>
      </Dialog>

      {pageLoaded && tracker && supervisor && (
        <MDBox mb={5}>
          <MDBox mt={0} mb={1} display="flex" justifyContent="space-between">
            <Button
              color="dark"
              size="small"
              startIcon={<Icon>west</Icon>}
              onClick={() => navigate(`/inventory/trackers/${tracker?.reference}`)}
            >
              Back
            </Button>

            <MDBox display="flex" gap={1}>
              <MDButton
                size="small"
                variant="outlined"
                color="dark"
                startIcon={<Icon>add</Icon>}
                onClick={() => setOpenIssueDialog(true)}
              >
                Issue Stock
              </MDButton>

              <MDButton
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<Icon>undo</Icon>}
                onClick={() => setOpenRecallDialog(true)}
              >
                Recall Stock
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox mt={2}>
            <Card sx={{ marginTop: 0 }}>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h6" fontSize={48}>
                  <Icon>person</Icon>
                </MDTypography>

                <MDTypography variant="h4" fontWeight="medium">
                  {supervisor.name}
                </MDTypography>

                <MDTypography variant="button" color="text" fontWeight="light">
                  {tracker.title}
                </MDTypography>

                {/* <MDBox mt={3} display="flex" justifyContent="center" gap={6} flexWrap="wrap">
                  <MDBox textAlign="center">
                    <MDTypography variant="button" color="text" fontWeight="light">
                      Products
                    </MDTypography>
                    <MDTypography variant="h5" fontWeight="medium">
                      {formatNumber(summary.products)}
                    </MDTypography>
                  </MDBox>

                  <MDBox textAlign="center">
                    <MDTypography variant="button" color="text" fontWeight="light">
                      Available
                    </MDTypography>
                    <MDTypography variant="h5" fontWeight="medium">
                      {formatNumber(summary.available)}
                    </MDTypography>
                  </MDBox>

                  <MDBox textAlign="center">
                    <MDTypography variant="button" color="text" fontWeight="light">
                      Locked
                    </MDTypography>
                    <MDTypography variant="h5" fontWeight="medium">
                      {formatNumber(summary.locked)}
                    </MDTypography>
                  </MDBox>
                </MDBox> */}
              </MDBox>
            </Card>
          </MDBox>

          <MDBox mt={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={2.4}>
                <Card sx={{ marginTop: 0 }}>
                  <MDBox p={2}>
                    <MDTypography variant="h6" color="text" fontWeight="light">
                      Products
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="medium" mt={1}>
                      {formatNumber(summary.products)}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <Card sx={{ marginTop: 0 }}>
                  <MDBox p={2}>
                    <MDTypography variant="h6" color="text" fontWeight="light">
                      Available
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="medium" mt={1}>
                      {formatNumber(summary.available)}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <Card sx={{ marginTop: 0 }}>
                  <MDBox p={2}>
                    <MDTypography variant="h6" color="text" fontWeight="light">
                      Issued
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="medium" mt={1}>
                      {formatNumber(summary.issued)}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <Card sx={{ marginTop: 0 }}>
                  <MDBox p={2}>
                    <MDTypography variant="h6" color="text" fontWeight="light">
                      Transfered
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="medium" mt={1}>
                      {formatNumber(summary.transfered)}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <Card sx={{ marginTop: 0 }}>
                  <MDBox p={2}>
                    <MDTypography variant="h6" color="text" fontWeight="light">
                      Returned
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="medium" mt={1}>
                      {formatNumber(summary.returned)}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          </MDBox>

          <MDBox mt={3}>
            <Card sx={{ marginTop: 0 }}>
              <MDBox p={2}>
                <MDTypography variant="h6" fontWeight="medium">
                  Products
                </MDTypography>
              </MDBox>

              <DataTable
                table={{ columns: productColumns, rows: products }}
                isSorted
                entriesPerPage={false}
                showTotalEntries
                buttons
                noEndBorder
              />
            </Card>
          </MDBox>

          <MDBox mt={3}>
            <Card sx={{ marginTop: 0 }}>
              <MDBox p={2}>
                <MDTypography variant="h6" fontWeight="medium">
                  Recent Movements
                </MDTypography>
              </MDBox>

              <DataTable
                table={{ columns: recentColumns, rows: recent }}
                isSorted
                entriesPerPage={false}
                showTotalEntries
                buttons
                noEndBorder
              />
            </Card>
          </MDBox>
        </MDBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}
